import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { 
  FiSearch, FiTruck, FiCalendar, FiMapPin, 
  FiRefreshCw, FiFilter, FiEye,
  FiCheckCircle, FiClock, FiXCircle, FiAlertCircle
} from 'react-icons/fi';

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Obtenir la couleur en fonction du statut
const getStatusColor = (status) => {
  switch(status) {
    case 'confirmé':
    case 'confirmed':
    case 'accepted':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    case 'en_attente':
    case 'pending':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'annulé':
    case 'cancelled':
    case 'rejected':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    case 'terminé':
    case 'completed':
      return 'bg-blue-600/20 text-blue-400 border border-blue-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

const getStatusIcon = (status) => {
  switch(status) {
    case 'confirmé':
    case 'confirmed':
    case 'accepted':
      return <FiCheckCircle className="w-4 h-4" />;
    case 'en_attente':
    case 'pending':
      return <FiClock className="w-4 h-4" />;
    case 'annulé':
    case 'cancelled':
    case 'rejected':
      return <FiXCircle className="w-4 h-4" />;
    case 'terminé':
    case 'completed':
      return <FiCheckCircle className="w-4 h-4" />;
    default:
      return null;
  }
};

const getStatusLabel = (status) => {
  const labels = {
    'confirmé': 'Confirmé',
    'confirmed': 'Confirmé',
    'accepted': 'Accepté',
    'en_attente': 'En attente',
    'pending': 'En attente',
    'annulé': 'Annulé',
    'cancelled': 'Annulé',
    'rejected': 'Annulé',
    'terminé': 'Terminé',
    'completed': 'Terminé'
  };
  return labels[status] || status;
};

function AdminMoves() {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadMoves();
  }, []);

  const loadMoves = async () => {
    try {
      setLoading(true);
      const response = await api.getAllReservations();
      
      if (response && response.success) {
        // Normaliser les données pour s'assurer que tous les champs sont présents
        const normalizedMoves = (response.data || []).map(move => ({
          ...move,
          client_name: move.client_name || 'Client inconnu',
          from_address: move.from_address || 'N/A',
          to_address: move.to_address || 'N/A',
          move_date: move.move_date || move.requested_at || move.created_at,
          status: move.status || 'pending'
        }));
        setMoves(normalizedMoves);
      } else {
        throw new Error(response.message || 'Erreur lors du chargement des déménagements');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des déménagements:', error);
      setError(error.message || 'Impossible de charger les déménagements');
      setMoves([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredMoves = moves.filter(move => {
    const matchesSearch = 
      move.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      move.from_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      move.to_address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'en_attente' && (move.status === 'en_attente' || move.status === 'pending')) ||
      (filterStatus === 'confirmé' && (move.status === 'confirmé' || move.status === 'confirmed' || move.status === 'accepted' || move.status === 'in_progress' || move.status === 'collection_completed')) ||
      (filterStatus === 'terminé' && (move.status === 'terminé' || move.status === 'completed')) ||
      (filterStatus === 'annulé' && (move.status === 'annulé' || move.status === 'cancelled' || move.status === 'rejected')) ||
      move.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const paginatedMoves = filteredMoves.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredMoves.length / itemsPerPage);

  const statusCounts = {
    all: moves.length,
    en_attente: moves.filter(m => m.status === 'en_attente' || m.status === 'pending').length,
    confirmé: moves.filter(m =>
      m.status === 'confirmé' || m.status === 'confirmed' || m.status === 'accepted' ||
      m.status === 'in_progress' || m.status === 'collection_completed'
    ).length,
    terminé: moves.filter(m => m.status === 'terminé' || m.status === 'completed').length,
    annulé: moves.filter(m => m.status === 'annulé' || m.status === 'cancelled' || m.status === 'rejected').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-yellow-400 mb-2">
          Gestion des déménagements
        </h1>
        <p className="text-gray-400 font-medium">Suivez et gérez tous les déménagements</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <FiAlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <p className="text-sm font-semibold text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-300">
              <FiXCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Total</p>
            <FiTruck className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{statusCounts.all}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">En attente</p>
            <FiClock className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{statusCounts.en_attente}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Confirmés</p>
            <FiCheckCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{statusCounts.confirmé}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Terminés</p>
            <FiCheckCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{statusCounts.terminé}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Annulés</p>
            <FiXCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{statusCounts.annulé}</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par client ou adresse..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500 transition-all"
            />
          </div>
          <div className="flex items-center space-x-3">
            <FiFilter className="text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="en_attente">En attente</option>
              <option value="confirmé">Confirmé</option>
              <option value="terminé">Terminé</option>
              <option value="annulé">Annulé</option>
            </select>
          </div>
          <button
            onClick={loadMoves}
            className="px-5 py-3 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all duration-300 flex items-center font-semibold"
          >
            <FiRefreshCw className="mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Moves Table */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  De
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  À
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-700"></div>
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent border-t-yellow-500 absolute top-0 left-0"></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : paginatedMoves.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    Aucun déménagement trouvé
                  </td>
                </tr>
              ) : (
                paginatedMoves.map((move) => (
                  <tr key={move.id} className="hover:bg-gray-700/50 transition-all duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-white">
                        {move.client_name || 'N/A'}
                      </div>
                      {move.client_email && (
                        <div className="text-xs text-gray-400 mt-1">{move.client_email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300 max-w-xs truncate flex items-center" title={move.from_address}>
                        <FiMapPin className="mr-2 text-gray-500 flex-shrink-0" />
                        {move.from_address || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300 max-w-xs truncate flex items-center" title={move.to_address}>
                        <FiMapPin className="mr-2 text-gray-500 flex-shrink-0" />
                        {move.to_address || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-400 flex items-center">
                        <FiCalendar className="mr-2 text-gray-500" />
                        {formatDate(move.move_date || move.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1.5 inline-flex items-center text-xs leading-5 font-bold rounded-full ${getStatusColor(move.status)}`}>
                        {getStatusIcon(move.status)}
                        <span className="ml-1.5">{getStatusLabel(move.status || 'en_attente')}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <Link
                        to={`/admin/moves/${move.id}`}
                        className="text-yellow-400 hover:text-yellow-300 hover:underline flex items-center transition-colors"
                      >
                        <FiEye className="mr-1" />
                        Voir
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-900 px-6 py-4 flex items-center justify-between border-t border-gray-700">
            <div className="text-sm text-gray-400 font-medium">
              Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredMoves.length)} sur {filteredMoves.length} déménagements
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-semibold bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-semibold bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminMoves;
